import React, { useRef, type ReactElement } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  toast,
} from "@ndd/ui";
import type { RdScheduleBlock } from "../../../../shared/rd-schedule.js";
import {
  REMOVE_ONCE_NOTE,
  REMOVE_REPEAT_NOTE,
  blockOccurrenceSummary,
} from "../../lib/rd-block-time.js";
import { WEEK_GRID_SELECTOR, ownBlockKey } from "../../lib/rd-schedule-week.js";
import {
  removeFailureOf,
  useRemoveRdTimeBlock,
} from "../../lib/use-remove-rd-time-block.js";

// NO-877: Remove for the RD's own blocked time. Opening a blocked-time block on
// the Week shows what it is and asks before it goes. A block can be removed but
// not edited, so this is the only change the Week offers.

export interface RemoveBlockTimeBodyProps {
  block: RdScheduleBlock;
  timeZone: string;
  failure: string | null;
  removing: boolean;
  onRemove: () => void;
  onClose: () => void;
}

/** The dialog's content, presentational so every state renders without a portal. */
export function RemoveBlockTimeBody(props: RemoveBlockTimeBodyProps): ReactElement {
  const { block } = props;

  return (
    <>
      <DialogHeader>
        <DialogTitle>Remove this blocked time?</DialogTitle>
        <DialogDescription>
          {blockOccurrenceSummary(block, props.timeZone)}
        </DialogDescription>
      </DialogHeader>
      {block.description ? (
        <p className="m-0 text-body text-foreground">{block.description}</p>
      ) : null}
      <p className="m-0 text-body text-muted-strong">
        {block.repeat === "weekly" ? REMOVE_REPEAT_NOTE : REMOVE_ONCE_NOTE}
      </p>
      {props.failure ? (
        <p role="alert" className="m-0 text-body text-destructive">
          {props.failure}
        </p>
      ) : null}
      <DialogFooter>
        <Button
          type="button"
          variant="utility"
          size="compact"
          disabled={props.removing}
          onClick={props.onClose}
        >
          Keep it
        </Button>
        <Button
          type="button"
          variant="destructive"
          size="compact"
          disabled={props.removing}
          onClick={props.onRemove}
        >
          {props.removing ? "Removing…" : "Remove"}
        </Button>
      </DialogFooter>
    </>
  );
}

export function RemoveBlockTimeDialog({
  block,
  timeZone,
  rdId,
  onClose,
}: {
  /** The block that was opened; null keeps the dialog closed. */
  block: RdScheduleBlock | null;
  timeZone: string;
  rdId: string | null;
  onClose: () => void;
}): ReactElement {
  const openedKey = useRef<string | null>(null);
  const remove = useRemoveRdTimeBlock(rdId);

  if (block) openedKey.current = ownBlockKey(block);

  const close = (): void => {
    remove.reset();
    onClose();
  };

  // A removal on its way is not interrupted by closing, so its failure is seen.
  const handleOpenChange = (open: boolean): void => {
    if (!open && !remove.isPending) close();
  };

  return (
    <Dialog open={block !== null} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-[480px]"
        onCloseAutoFocus={(event) => returnFocusToWeek(event, openedKey.current)}
      >
        {block ? (
          <RemoveBlockTimeBody
            block={block}
            timeZone={timeZone}
            failure={remove.isError ? removeFailureOf(remove.error) : null}
            removing={remove.isPending}
            onRemove={() =>
              remove.mutate(block.id, {
                onSuccess: () => {
                  toast.success("Blocked time removed");
                  close();
                },
              })
            }
            onClose={close}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Focus goes back to the block that opened this, or to the Week grid when
 * Remove took that block away, so it never falls to the page body.
 */
function returnFocusToWeek(event: Event, blockKey: string | null): void {
  event.preventDefault();

  const opener = blockKey
    ? document.querySelector<HTMLElement>(`[data-block-key="${CSS.escape(blockKey)}"]`)
    : null;

  (opener ?? document.querySelector<HTMLElement>(WEEK_GRID_SELECTOR))?.focus();
}
